/**
 * RESPONSIBILITY: hold the flag definitions. Registration and lookup only —
 * no storage, no built-ins, no encoding.
 *
 * EVERY FLAG DECLARES ITS OWN CODE: a single character from the base64url alphabet.
 * The code is the flag's identity in a URL, so it is permanent — changing one
 * silently changes what every shared link means.
 *
 * Codes are CASE-SENSITIVE. 'a' and 'A' are two different flags.
 */
import { FLAG_ALPHABET, MAX_FLAGS, isLegalCode } from './alphabet';

export { MAX_FLAGS };

export interface FlagDef {
  /** One character, A-Z a-z 0-9 - _ . Unique and permanent. */
  code: string;
  key: string;
  label: string;
  description: string;
  /** Off unless stated. A flag nobody has touched must not change behaviour. */
  default?: boolean;
}

const byKey = new Map<string, FlagDef>();
const byCode = new Map<string, FlagDef>();

/** Returns the key, so call sites can `export const X = registerFlag({...})`. */
export function registerFlag(def: FlagDef): string {
  const { code, key } = def;

  if (!isLegalCode(code)) {
    throw new Error(
      `Flag "${key}" has code "${code}". It must be exactly one character from: ` +
        `${FLAG_ALPHABET} — case-sensitive, and URL-safe without escaping.`,
    );
  }

  const clash = byCode.get(code);
  if (clash && clash.key !== key) {
    throw new Error(
      `Flag "${key}" wants code "${code}", already used by "${clash.key}". ` +
        `Codes are permanent — pick a free one: ${freeCodes().slice(0, 12).join(' ')}…`,
    );
  }

  const existing = byKey.get(key);
  if (existing && existing.code !== code) {
    throw new Error(
      `Flag "${key}" is already registered as "${existing.code}", not "${code}". ` +
        `Changing a code changes what every shared link means.`,
    );
  }

  const stored: FlagDef = { ...def, default: def.default ?? false };
  byKey.set(key, stored);
  byCode.set(code, stored);
  return key;
}

/** Sorted by code, so display order is stable and matches the alphabet. */
export const allFlags = (): FlagDef[] =>
  [...byKey.values()].sort(
    (a, b) => FLAG_ALPHABET.indexOf(a.code) - FLAG_ALPHABET.indexOf(b.code),
  );

export const isKnownFlag = (key: string): boolean => byKey.has(key);
export const flagByKey = (key: string): FlagDef | undefined => byKey.get(key);
export const flagByCode = (code: string): FlagDef | undefined => byCode.get(code);

export const flagDefaults = (): Record<string, boolean> =>
  Object.fromEntries(allFlags().map((f) => [f.key, f.default === true]));

/** Unused codes, for whoever registers the next flag. */
export const freeCodes = (): string[] =>
  [...FLAG_ALPHABET].filter((c) => !byCode.has(c));
